import { ActionIcon, Alert, Button, Group, Menu, Stack, Text, Title } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconLogin, IconLogout } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Badge from 'components/Badge';
import BadgeStatus from 'components/Badge/BadgeStatus';
import Breadcrumbs from 'components/BreadcrumbsComponent';
import dayjs from 'dayjs';
import { useAuth } from 'hooks/useAuth';
// import ActivityListSection from 'modules/pages/lcms/courses/ActivityListSection';
import BasicInformationSection from 'modules/pages/lcms/courses/BasicInformationSection';
import BeelajarLMSSection from 'modules/pages/lcms/courses/BeelajarLMSSection';
import CheckInCheckOutHistorySection from 'modules/pages/lcms/courses/CheckInCheckOutHistorySection';
import DeliveryDetail from 'modules/pages/lcms/courses/DeliveryDetail';
import IndicatorStandarsSection from 'modules/pages/lcms/courses/IndicatorStandarsSection';
import LoaderCourseDetail from 'modules/pages/lcms/courses/LoaderCourseDetail';
import ModalCheckInOut from 'modules/pages/lcms/courses/ModalCheckInOut';
import ModalDelete from 'modules/pages/lcms/courses/ModalDelete';
import PICMaterialSection from 'modules/pages/lcms/courses/PICMaterialSection';
import ReviewApproval from 'modules/pages/lcms/courses/ReviewApproval';
// import ScheduleSection from 'modules/pages/lcms/courses/ScheduleSection';
import SMEMaterialSection from 'modules/pages/lcms/courses/SMEMaterialSection';
import React, { useMemo } from 'react';
import { HiOutlineDotsVertical, HiOutlinePencilAlt, HiOutlineTrash } from 'react-icons/hi';
import { useNavigate, useParams } from 'react-router-dom';
import { pathnames } from 'routes/lcms';
import { beelajarService } from 'services/beelajar/beelajar.service';
import { courseService } from 'services/lcms/course.service';
import { useCourseStore } from 'store/createCourseStore';
import { BasicInformation } from 'types/lcms/courses';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';

const DetailCoursePage = () => {
  const { profile } = useAuth();
  const params = useParams();
  const queryClient = useQueryClient();
  const isDesktop = useMediaQuery('(min-width: 1440px)');
  const isTablet = useMediaQuery('(min-width: 757px)');
  const navigation = useNavigate();
  const { setFormMode } = useCourseStore();
  const { id } = params;
  const { data, isLoading } = useQuery({
    queryKey: ['lcmsDetail', id],
    queryFn: () => courseService.detail(id as string),
    select: (response) => response.data
  });
  const [openedCheckIn, { open: openModalCheckIn, close: closeModalCheckIn }] =
    useDisclosure(false);
  const [openedCheckOut, { open: openModalCheckOut, close: closeModalCheckOut }] =
    useDisclosure(false);
  const [openedDelete, { open: openModalDelete, close: closeModalDelete }] = useDisclosure(false);

  const breadcrumbs = [
    { title: 'Course', href: pathnames.courses },
    { title: 'Detail Course', href: '' }
  ];

  const detailData = data?.data;

  const checkInBeelajarLMS = useMutation({
    mutationFn: courseService.checkInBeelajarLMS,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lcmsDetail', id] });

      showSuccessNotification({
        title: 'Success',
        message: 'Check in Beelajar LMS successfully'
      });

      closeModalCheckIn();
    },
    onError: (error) => {
      showErrorNotification({
        title: 'Error when try start revise course',
        message: error.message
      });
      closeModalCheckIn();
    }
  });

  const beelajarLogin = useMutation({
    mutationFn: beelajarService.loginBeelajarService,
    onSuccess: async (data, variables) => {
      const linkMaterial = detailData?.beelajarID
        ? `${process.env.REACT_APP_BEELAJAR_URL?.replace(
            '/webservice/rest/server.php',
            ''
          )}/course/view.php?id=${detailData?.beelajarID}`
        : '';
      const url = data?.data?.loginurl + '&wantsurl=' + linkMaterial;

      if (variables.isSecondRequest) {
        window.open(url, '_blank');
        await checkInBeelajarLMS.mutateAsync(id as string);
      }
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Error Unauthorized Access',
        message: error?.response?.data?.message || 'Failed to start revise course'
      });
      closeModalCheckIn();
    }
  });

  const checkOut = useMutation({
    mutationFn: (data: { courseId: string; callback?: () => void }) =>
      courseService.checkOutBeelajarLMS(data.courseId),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lcmsDetail', id] });
      showSuccessNotification({
        title: 'Success',
        message: 'To end revise on Beelajar LMS successfully'
      });

      // Call the callback after checkout is successful
      if (variables.callback) {
        variables.callback();
      }
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Error when try end revise course',
        message: error?.response?.data?.message || 'Failed to end revise course'
      });
    }
  });

  const deleteCourse = useMutation({
    mutationFn: courseService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lcmsCourses'] });
      showSuccessNotification({
        title: 'Success',
        message: 'Course deleted successfully'
      });
      navigation(pathnames.courses);
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Error when delete course',
        message: error?.response?.data?.message || 'Failed to delete course'
      });
    }
  });

  const { checkInOutHistory } = detailData || {};

  // Find the most recent check-in and check-out entries from the history
  const checkInBy = useMemo(() => {
    const checkInEntries =
      checkInOutHistory?.filter((item) => item.status === 'CheckedIn' && item.checkInDateTime) ||
      [];
    if (checkInEntries.length === 0) return null;

    // Sort by checkInDateTime descending to get the most recent
    return checkInEntries.sort(
      (a, b) => dayjs(b.checkInDateTime).valueOf() - dayjs(a.checkInDateTime).valueOf()
    )[0];
  }, [checkInOutHistory]);

  const checkOutBy = useMemo(() => {
    const checkOutEntries =
      checkInOutHistory?.filter((item) => item.status === 'CheckedOut' && item.checkOutDateTime) ||
      [];
    if (checkOutEntries.length === 0) return null;

    // Sort by checkOutDateTime descending to get the most recent
    return checkOutEntries.sort(
      (a, b) => dayjs(b.checkOutDateTime).valueOf() - dayjs(a.checkOutDateTime).valueOf()
    )[0];
  }, [checkInOutHistory]);

  // Apply the same logic as schema.tsx
  let showCheckIn = false;
  let showCheckOut = false;

  if (!checkInBy?.checkInDateTime && !checkOutBy?.checkOutDateTime) {
    showCheckIn = true;
  } else if (checkInBy?.checkInDateTime && !checkOutBy?.checkOutDateTime) {
    showCheckIn = false;
  } else if (
    checkOutBy &&
    checkInBy?.checkInDateTime &&
    dayjs(checkInBy.checkInDateTime).isAfter(dayjs(checkOutBy.checkOutDateTime))
  ) {
    showCheckIn = false;
  } else if (
    checkOutBy &&
    checkInBy?.checkInDateTime &&
    dayjs(checkInBy.checkInDateTime).isBefore(dayjs(checkOutBy.checkOutDateTime))
  ) {
    showCheckIn = true;
  }

  if (
    checkInBy?.checkInDateTime &&
    (!checkOutBy?.checkOutDateTime ||
      dayjs(checkInBy.checkInDateTime).isAfter(dayjs(checkOutBy.checkOutDateTime)))
  ) {
    showCheckOut = true;
  } else if (
    checkOutBy &&
    checkInBy?.checkInDateTime &&
    dayjs(checkInBy.checkInDateTime).isAfter(dayjs(checkOutBy.checkOutDateTime))
  ) {
    showCheckOut = false;
  }

  let reviseBy = '';
  if (showCheckIn && showCheckOut) {
    reviseBy = checkInOutHistory?.pop()?.name ?? '';
  } else if (showCheckIn && !showCheckOut) {
    reviseBy = checkInBy?.name ?? '';
  }

  const Icon = '⚠️';

  const onConfirmInOut = (callback: () => void) => {
    if (openedCheckIn) {
      // Start with beelajarLogin first, then checkInBeelajarLMS will be called in onSuccess
      if (profile?.adminEmail) {
        beelajarLogin.mutate({ email: profile.adminEmail, isSecondRequest: false });
        setTimeout(() => {
          beelajarLogin.mutate({ email: profile.adminEmail, isSecondRequest: true });
        }, 500);
      }
      return;
    } else {
      checkOut.mutate({
        courseId: id as string,
        callback
      });
      closeModalCheckOut();
      return;
    }
  };

  const handleCloseModalCheckInOut = () => {
    if (openedCheckIn) {
      closeModalCheckIn();
    } else {
      closeModalCheckOut();
    }
  };

  const onDelete = () => {
    return deleteCourse.mutate(id as string);
  };

  return (
    <>
      <Breadcrumbs items={breadcrumbs} mb={32} />
      {isLoading && <LoaderCourseDetail />}
      {!isLoading && (
        <>
          <Stack gap={4}>
            <Group align='start' justify='space-between'>
              <Group align='start' justify='start'>
                <Title order={2} maw={600}>
                  {data?.data?.basicInformation?.courseName}
                </Title>
                <BadgeStatus status={(data?.data?.status as any) ?? 'draft'} styleVariant='solid' />
                {data?.data?.version[0]?.history && (
                  <Badge styleVariant='default'>
                    Version {data?.data?.version[0]?.history ?? '1'}
                  </Badge>
                )}
              </Group>
              {isTablet && (
                <Menu shadow='md'>
                  <Menu.Target>
                    <ActionIcon variant='transparent' c={'dark'}>
                      <HiOutlineDotsVertical />
                    </ActionIcon>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<HiOutlinePencilAlt size={14} />}
                      onClick={() => {
                        setFormMode('edit');
                        navigation(pathnames.courses + `/edit/${id}`);
                      }}
                      disabled={!showCheckIn}
                    >
                      Edit
                    </Menu.Item>
                    <Menu.Item
                      color='red'
                      leftSection={<HiOutlineTrash size={14} />}
                      disabled={!showCheckIn}
                      onClick={() => openModalDelete()}
                    >
                      Delete
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
          </Stack>
          <Group mt={24} align='flex-start' pos={'relative'}>
            <Stack maw={isDesktop ? 970 : '100%'} flex={1}>
              {checkInOutHistory?.length === 0 ? (
                <Alert variant='light' icon={Icon} color='yellow'>
                  <Group justify='space-between' align='flex-start'>
                    <Text fw={500}>You have not started revise yet</Text>
                    <div>
                      <Button
                        color='green.8'
                        leftSection={<IconLogin />}
                        loading={checkInBeelajarLMS.isPending}
                        onClick={() => openModalCheckIn()}
                      >
                        Start Revise
                      </Button>
                    </div>
                  </Group>
                </Alert>
              ) : (
                <Alert variant='light' icon={Icon} color='yellow'>
                  <Group justify={showCheckIn ? 'space-between' : 'flex-start'} align='flex-start'>
                    <Text fw={500}>
                      Last update for revise this course on Beelajar LMS
                      {reviseBy && ` by ${reviseBy}`}
                    </Text>

                    <div>
                      <Button
                        color={showCheckIn ? 'green.8' : 'destructive'}
                        loading={checkInBeelajarLMS.isPending || checkOut.isPending}
                        leftSection={showCheckIn ? <IconLogin /> : <IconLogout />}
                        onClick={() => (showCheckIn ? openModalCheckIn() : openModalCheckOut())}
                      >
                        {`${showCheckIn ? 'Start' : 'End'} Revise`}
                      </Button>
                    </div>
                  </Group>
                </Alert>
              )}
              <BasicInformationSection
                beelajarID={data?.data?.beelajarID}
                bcldModuleCode={data?.data?.bcldModuleCode}
                basicInformation={
                  {
                    ...data?.data?.basicInformation,
                    creatorCode: data?.data?.creatorCode
                  } as BasicInformation & { creatorCode?: string }
                }
                learningObjectives={data?.data?.deliveryDetails?.learningObjectives}
              />
              {/* <ActivityListSection activities={data?.data?.activities || []} /> */}
              {/* {data?.data?.beelajarID ? ( */}
              <BeelajarLMSSection beelajarId={data?.data?.beelajarID as string} />
              {/* ) : null} */}
              <SMEMaterialSection
                data={data?.data?.reviewApproval}
                courseId={data?.data?.msLCMSCourseID as string}
              />
              <PICMaterialSection
                data={data?.data?.reviewApproval}
                courseId={data?.data?.msLCMSCourseID as string}
              />
              <CheckInCheckOutHistorySection
                data={data?.data?.checkInOutHistory || []}
                versions={data?.data?.version || []}
              />
            </Stack>
            <Stack w={isDesktop ? 412 : '100%'} pos={isDesktop ? 'sticky' : 'relative'} top={16}>
              {/* <ScheduleSection data={data?.data?.basicInformation} /> */}
              <DeliveryDetail data={data?.data?.deliveryDetails} />
              <IndicatorStandarsSection data={data?.data?.indicatorStandards} />
              <ReviewApproval data={data?.data?.reviewApproval} />
            </Stack>
          </Group>

          <ModalCheckInOut
            onClose={handleCloseModalCheckInOut}
            onConfirm={onConfirmInOut}
            courseId={id as string}
            opened={openedCheckIn || openedCheckOut}
            type={openedCheckIn ? 'checkin' : 'checkout'}
            isLoading={checkInBeelajarLMS.isPending || checkOut.isPending}
          />

          <ModalDelete
            title='Delete Data'
            description={`You are about to delete course ${detailData?.basicInformation?.courseName}. Are you sure to delete it?`}
            labelConfirm='Delete'
            onClose={() => closeModalDelete()}
            onConfirm={onDelete}
            opened={openedDelete}
            isLoading={deleteCourse.isPending}
          />
        </>
      )}
    </>
  );
};

export default DetailCoursePage;
